import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import Logo from "./Logo";

const NAV = [
    { to: "/services", label: "Services" },
    { to: "/industries", label: "Industries" },
    { to: "/about", label: "About" },
    { to: "/resources", label: "Resources" },
    { to: "/contact", label: "Contact" },
];

const navLinkBase =
    "text-[15px] font-medium hover:text-brand-teal transition-colors duration-200 py-2";

export const Header = () => {
    const [open, setOpen] = useState(false);
    const drawerRef = useRef(null);
    const triggerRef = useRef(null);
    const location = useLocation();

    // Close on route change
    useEffect(() => { setOpen(false); }, [location.pathname]);

    // Lock body scroll while drawer is open
    useEffect(() => {
        if (open) {
            const prev = document.body.style.overflow;
            document.body.style.overflow = "hidden";
            return () => { document.body.style.overflow = prev; };
        }
    }, [open]);

    // Escape closes drawer + focus trap
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key === "Escape") {
                setOpen(false);
                triggerRef.current?.focus();
            }
            if (e.key === "Tab" && drawerRef.current) {
                const focusables = drawerRef.current.querySelectorAll(
                    'a, button, input, [tabindex]:not([tabindex="-1"])'
                );
                if (focusables.length === 0) return;
                const first = focusables[0];
                const last = focusables[focusables.length - 1];
                if (e.shiftKey && document.activeElement === first) {
                    e.preventDefault(); last.focus();
                } else if (!e.shiftKey && document.activeElement === last) {
                    e.preventDefault(); first.focus();
                }
            }
        };
        document.addEventListener("keydown", onKey);
        // Focus first link on open
        const f = drawerRef.current?.querySelector("a, button");
        f?.focus();
        return () => document.removeEventListener("keydown", onKey);
    }, [open]);

    return (
        <>
            <header
                className="sticky top-0 z-40 w-full bg-white/95 backdrop-blur border-b border-brand-border"
                data-testid="site-header"
            >
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-[72px]">
                        <Link
                            to="/"
                            aria-label="AccessNova — home"
                            className="flex items-center"
                            data-testid="header-logo-link"
                        >
                            <Logo className="h-9 sm:h-10 w-auto" />
                        </Link>

                        {/* Desktop nav */}
                        <nav aria-label="Primary" className="hidden lg:flex items-center gap-8">
                            {NAV.map((n) => (
                                <NavLink
                                    key={n.to}
                                    to={n.to}
                                    className={({ isActive }) =>
                                        `${navLinkBase} ${isActive ? "text-brand-teal" : "text-brand-text"}`
                                    }
                                    data-testid={`nav-link-${n.label.toLowerCase()}`}
                                >
                                    {n.label}
                                </NavLink>
                            ))}
                        </nav>

                        <div className="flex items-center gap-3">
                            <Link
                                to="/free-audit"
                                className="hidden sm:inline-flex items-center justify-center rounded-md bg-brand-teal text-white font-semibold px-5 py-3 text-sm hover:bg-brand-tealBright transition-colors duration-200 touch-target"
                                data-testid="header-cta-free-audit"
                            >
                                Free Mini Audit
                            </Link>

                            <button
                                ref={triggerRef}
                                onClick={() => setOpen((o) => !o)}
                                aria-expanded={open}
                                aria-controls="mobile-drawer"
                                aria-label={open ? "Close menu" : "Open menu"}
                                className="lg:hidden inline-flex items-center justify-center rounded-md border border-brand-border touch-target text-brand-navy hover:bg-brand-soft transition-colors"
                                data-testid="header-mobile-toggle"
                            >
                                {open ? <X size={22} aria-hidden="true" /> : <Menu size={22} aria-hidden="true" />}
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Mobile drawer — rendered OUTSIDE <header> so backdrop-blur stacking context
                doesn't trap the fixed positioning inside the 72px header. */}
            {open && (
                <div
                    className="lg:hidden fixed inset-0 z-[60]"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Mobile navigation"
                >
                    <div
                        className="absolute inset-0 bg-black/50"
                        onClick={() => setOpen(false)}
                        aria-hidden="true"
                        data-testid="mobile-drawer-overlay"
                    />
                    <div
                        ref={drawerRef}
                        id="mobile-drawer"
                        className="absolute right-0 top-0 h-full w-[85%] max-w-sm bg-white shadow-2xl flex flex-col"
                    >
                        <div className="flex items-center justify-between p-4 border-b border-brand-border">
                            <Logo className="h-8 w-auto" />
                            <button
                                onClick={() => setOpen(false)}
                                aria-label="Close menu"
                                className="rounded-md touch-target inline-flex items-center justify-center text-brand-navy hover:bg-brand-soft"
                                data-testid="mobile-drawer-close"
                            >
                                <X size={22} aria-hidden="true" />
                            </button>
                        </div>
                        <nav aria-label="Mobile primary" className="flex-1 overflow-y-auto p-4">
                            <ul className="space-y-1">
                                {NAV.map((n) => (
                                    <li key={n.to}>
                                        <NavLink
                                            to={n.to}
                                            className={({ isActive }) =>
                                                `block rounded-md px-3 py-3 text-base font-medium transition-colors ${
                                                    isActive
                                                        ? "bg-brand-soft text-brand-teal"
                                                        : "text-brand-text hover:bg-brand-soft"
                                                }`
                                            }
                                            data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                                        >
                                            {n.label}
                                        </NavLink>
                                    </li>
                                ))}
                            </ul>
                        </nav>
                        <div className="p-4 border-t border-brand-border">
                            <Link
                                to="/free-audit"
                                className="w-full inline-flex items-center justify-center rounded-md bg-brand-teal text-white font-semibold px-5 py-3 hover:bg-brand-tealBright transition-colors"
                                data-testid="mobile-cta-free-audit"
                            >
                                Free Mini Audit
                            </Link>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Header;
